// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Description
//
// Records and quantizes user input events from a variet of inputs:
// spacebar, midi, etc...
import * as Mousetrap from 'mousetrap';
import Rhythm from './rhythm';
import Pointer from './pointer';
import MidiInput from './midiInput';
import utils from '../utils';
import type MusicalClock from './musicalClock';
import type TimeSignature from './timeSignature';

const debug = false;

interface RecorderOptions {
  quantUnit?: Rhythm | string;
  spaceBar?: boolean;
  midi?: boolean;
}

interface RawEvent {
  type?: 'noteOn' | 'noteOff';
  midiNumber: string | number;
  velocity?: number;
}

export interface RecorderEvent extends RawEvent {
  /** not quantized */
  performanceStartTime: number;
  /** not quantized */
  performanceStartPointer: Pointer;
  /** not quantized */
  performanceStopTime?: number;
  /** not quantized */
  performanceStopPointer?: Pointer;
  /** quantized */
  startTime?: number;
  /** quantized */
  startPointer?: Pointer;
  /** not quantized??? */
  stopTime?: number;
  /** not quantized??? */
  stopPointer?: Pointer;
  /** percentage of staccato */
  staccato?: number;
}

const cloneEvt = utils.clone;

class Recorder {
  options: RecorderOptions;

  clock: MusicalClock;

  meter: TimeSignature;

  context: AudioContext;

  quantUnit: Rhythm;

  /** Quantizations per beat */
  quantsPerBeat: number;

  /** Quantizations per second */
  quantSec: number;

  /** Callbacks called when recording stops */
  stopCallbacks: ((evts: RecorderEvent[], stopPointer: Pointer) => void)[] = [];

  /** Callbacks called on any event */
  realTimeCallbacks: Function[] = [];

  /** Pointer to start of recording */
  // startPointer: Pointer;

  /** Pointer to start of recording */
  startPointer: Pointer;

  /** Seconds to start of recording */
  startTime: number;

  /** Pointer to stop of recording */
  stopPointer: Pointer;

  // stopPointer: Pointer;

  /** Time in seconds at which to stop recording */
  stopTime: number;

  /** Are we currently recording? */
  recording = false;

  events: RecorderEvent[];

  midiListener: MidiInput;

  constructor(
    clock: MusicalClock,
    options: RecorderOptions = { quantUnit: 'e', spaceBar: true, midi: true },
  ) {
    //      debug = true;
    this.clock = clock;
    this.meter = clock.meter;
    this.context = clock.context;

    this.quantUnit = new Rhythm(options.quantUnit);
    this.setQuantUnit();

    this.events = [];

    if (options.midi) this.initializeMidi();

    this.options = options;
    this.startPointer = new Pointer('0:0:0', this.meter);
    this.stopPointer = new Pointer('0:0:0', this.meter);
    this.startTime = 0;
    this.stopTime = 0;

    return this;
  }

  record([startPointer, stopPointer]: [Pointer, Pointer]) {
    if (!startPointer || !(startPointer instanceof Pointer))
      throw new Error('startPointer required to record.');
    if (!stopPointer || !(stopPointer instanceof Pointer))
      throw new Error('stopPointer required to record.');

    if (!this.options.spaceBar) this.disableSpaceListener();
    if (this.options.spaceBar) this.initSpaceListener();
    this.startPointer = startPointer;
    this.stopPointer = stopPointer;
    this.startTime = this.clock.getSeconds(this.startPointer);
    this.events = [];
    this.recording = true;
  }

  stop(stopPointer: Pointer = this.clock.getPointer()) {
    if (this.options.spaceBar) this.disableSpaceListener();
    this.stopPointer = stopPointer;
    this.stopTime = this.clock.getSeconds(stopPointer);

    // NoteOff any events that haven't been stopped:
    const lastEvent = this.events[this.events.length - 1];
    if (this.events.length > 0 && typeof lastEvent.stopTime === 'undefined') {
      lastEvent.stopTime = this.stopTime - this.startTime;
      lastEvent.performanceStopTime = lastEvent.stopTime;
      lastEvent.stopPointer = this.stopPointer.clone().subtract(this.startPointer);
      lastEvent.performanceStopPointer = lastEvent.stopPointer.clone();
    }

    this.recording = false;

    // Quantize the last stop time:
    this.quantizeStopTime(this.events[this.events.length - 1]);

    // Calculate the last staccato:
    this.calculateStaccato();

    // Clean the last event:
    this.cleanLast();

    // Log it out if needed:
    if (debug) this.logEvents();

    // Callback RT Listeners:
    this.realTimeTrack(this.events[this.events.length - 1]);

    // Callback any final listeners:
    this.callbackStopListeners(stopPointer);
  }

  cleanFirst() {
    const startTime = 0;
    const startPointer = new Pointer('0:0', this.meter);

    // If the first event doesn't begin at 0:0, add a rest
    // leading up to it.
    if (!this.events[0].startPointer.equals('0:0')) {
      const restEvent: RecorderEvent = {
        type: 'noteOn',
        startTime,
        stopTime: this.events[0].startTime,
        startPointer,
        stopPointer: this.events[0].startPointer.clone(),
        performanceStartTime: startTime,
        performanceStopTime: this.events[0].startTime,
        performanceStartPointer: startPointer,
        performanceStopPointer: this.events[0].startPointer.clone(),
        midiNumber: 'r',
      };

      this.events.unshift(restEvent);
    }
  }

  cleanLast() {
    const startTime = 0;
    const startPointer = new Pointer('0:0', this.meter);
    const stopPointer = this.stopPointer.clone().subtract(this.startPointer);
    const stopTime = this.stopTime - this.startTime;

    // If there are NO events, create a long rest:
    if (this.events.length === 0) {
      this.events.push({
        startTime,
        stopTime,
        startPointer,
        stopPointer,
        performanceStartTime: startTime,
        performanceStopTime: stopTime,
        performanceStartPointer: startPointer,
        performanceStopPointer: stopPointer,
        midiNumber: 'r',
      });
      return;
    }

    // If the last event doesn't end on the stop pointer, add a rest
    // leading up to it.
    const lastEvent = this.events[this.events.length - 1];
    if (!stopPointer.equals(this.events[this.events.length - 1].stopPointer)) {
      this.events.push({
        startTime: lastEvent.stopTime,
        stopTime,
        startPointer: lastEvent.stopPointer.clone(),
        stopPointer,
        performanceStartTime: lastEvent.stopTime,
        performanceStopTime: stopTime,
        performanceStartPointer: lastEvent.stopPointer.clone(),
        performanceStopPointer: stopPointer,
        midiNumber: 'r',
      });
    }

    // If the last event is of 0 duration, delete it:
    if (lastEvent.stopPointer.equals(lastEvent.startPointer)) {
      this.events.length--;
    }
  }

  /**
   *
   * @param {Object} evt
   * @param {(number | string)} evt.midiNumber
   * @param {string} evt.type
   * @returns {RecorderEvent}
   */
  eventRecord(evt: RawEvent) {
    if (!this.recording) return;

    const performanceStartTime = this.clock.getSeconds() - this.startTime;
    const performanceStartPointer = this.clock.getPointer(performanceStartTime);

    const eventWithTiming = {
      performanceStartTime,
      performanceStartPointer,
      ...evt,
    };

    // If it's a noteOff, adjust previous event:
    if (evt.type === 'noteOff') {
      this.eventOffRecord(eventWithTiming);
      return;
    }

    // Otherwise it's a noteOn, quantize the performance startTime
    // and startPointer:
    const startPointer = performanceStartPointer.clone().quantizeTo(this.quantUnit);
    const startTime = this.clock.getSeconds(startPointer);

    const recorderEvent: RecorderEvent = {
      startPointer,
      startTime,
      performanceStartPointer,
      performanceStartTime,
      ...evt,
    };

    // Be sure the previous event's been stopped:
    this.previousEventOff(recorderEvent);

    // Add this event to the queue:
    this.events.push(recorderEvent);

    // If it's the first event, add a rest if needed:
    if (this.events.length === 1) this.cleanFirst();

    // If we've had more than one event, quantize
    // the stop time of the previous:
    const len = this.events.length;
    if (len >= 2) {
      this.quantizeStopTime(
        // event to quantize, next event
        this.events[len - 2],
        this.events[len - 1],
      );
      this.calculateStaccato();
    }

    // And notify any real time listeners of the noteOn event.
    this.realTimeTrack(recorderEvent);
  }

  /**
   * Find the matching noteOn event and add stop timings to it (unquantized)
   * @param stopEvent
   */
  eventOffRecord(
    stopEvent: RawEvent & { performanceStartTime: number; performanceStartPointer: Pointer },
  ) {
    let i = this.events.length - 1;
    for (i; i >= 0; i--) {
      if (
        this.events[i].midiNumber === stopEvent.midiNumber &&
        typeof this.events[i].performanceStopTime === 'undefined'
      ) {
        this.events[i].performanceStopTime = stopEvent.performanceStartTime;
        this.events[i].performanceStopPointer = stopEvent.performanceStartPointer.clone();
        if (typeof this.events[i].stopPointer === 'undefined') {
          this.events[i].stopPointer = stopEvent.performanceStartPointer.clone();
          this.events[i].stopTime = stopEvent.performanceStartTime;
        }

        break;
      }
    }
  }

  previousEventOff(evt: RecorderEvent) {
    const lastEvent = this.events[this.events.length - 1];
    if (this.events.length > 0 && typeof lastEvent.stopTime === 'undefined') {
      lastEvent.stopTime = evt.startTime;
      lastEvent.stopPointer = evt.startPointer.clone();
      lastEvent.performanceStopPointer = evt.performanceStartPointer.clone();
    }
  }

  logEvents() {
    this.events.forEach((evt) => {
      console.log(
        `Start: ${evt.performanceStartPointer} =>
        ${evt.startPointer}, ${evt.performanceStartTime} =>
        ${evt.startTime}
        \nStop: ${evt.performanceStopPointer} =>
        ${evt.stopPointer}, ${evt.performanceStopTime} =>
        ${evt.stopTime}`,
      );
    });
  }

  callbackStopListeners(stopPointer: Pointer) {
    this.stopCallbacks.forEach((callback) => {
      callback(this.cloneEvents(), stopPointer);
    });
  }

  addStopCallback(callback: (recorderEvents: RecorderEvent[]) => void, thisArg?: any) {
    if (typeof thisArg !== 'undefined') {
      this.stopCallbacks.push(callback.bind(thisArg));
    } else {
      this.stopCallbacks.push(callback);
    }
  }

  // Each time an event occurs, real time callbacks
  // are called with two arguments: the event, and
  // all of the events thus far.
  addRealTimeCallback(
    callback: (currentEvent: RecorderEvent, allEvents: RecorderEvent[]) => void,
    thisArg?: any,
  ) {
    if (typeof thisArg !== 'undefined') {
      this.realTimeCallbacks.push(callback.bind(thisArg));
    } else {
      this.realTimeCallbacks.push(callback);
    }
  }

  realTimeTrack(evt: RecorderEvent) {
    if (this.realTimeCallbacks.length > 0) {
      this.realTimeCallbacks.forEach((callback) => {
        callback(cloneEvt(evt), this.cloneEvents());
      });
    }
  }

  calculateStaccato() {
    this.events.forEach((evt) => {
      if (evt.midiNumber === 'r') return;
      if (typeof evt.stopTime === 'undefined' || typeof evt.performanceStopTime === 'undefined')
        return;
      const quantDuration = evt.stopTime - evt.startTime;
      const stacDuration = evt.performanceStopTime - evt.performanceStartTime;
      evt.staccato = +(stacDuration / quantDuration).toFixed(2);
    });
  }

  quantizeStopTime(evt: RecorderEvent, nextEvt?: RecorderEvent) {
    if (typeof evt === 'undefined') return;
    const roughQuant = evt.performanceStopPointer.clone().quantizeTo(this.quantUnit);
    const roughQuantTime = this.clock.getSeconds(roughQuant);

    // Set a default quantization:
    evt.stopPointer = roughQuant;
    evt.stopTime = roughQuantTime;

    // If there's no next event, be sure it's one quant unit long & return:
    if (typeof nextEvt === 'undefined') {
      if (evt.stopPointer.equals(evt.startPointer)) {
        evt.stopPointer.add(this.quantUnit);
        evt.stopTime = this.clock.getSeconds(evt.stopPointer);
      }
      return;
    }

    // If it's within half a quant of a beat,
    // quantize it to the beat:
    if (roughQuant.equals(evt.performanceStopPointer.getNextBeat())) {
      evt.stopPointer = roughQuant;
      evt.stopTime = roughQuantTime;
      return;
    }

    // If it's within half a quant of the next note,
    // Or within a quarter subdivision of the beat to the next note,
    // connect it to the next note:
    if (
      roughQuant.equals(nextEvt.startPointer) ||
      nextEvt.startPointer.clone().subtract(evt.performanceStopPointer).isLessThan('0:0:0.5')
    ) {
      evt.stopPointer = nextEvt.startPointer.clone();
      evt.stopTime = nextEvt.startTime;
      return;
    }

    // If it's under .25 seconds from the next note and
    // its the same note (time to lift & redepress key)
    if (
      nextEvt.startTime - evt.performanceStopTime < 0.25 &&
      evt.midiNumber === nextEvt.midiNumber
    ) {
      evt.stopPointer = nextEvt.startPointer.clone();
      evt.stopTime = nextEvt.startTime;
      return;
    }

    // Otherwise, be sure it's one unit long and be done:
    if (evt.stopPointer.equals(evt.startPointer)) {
      evt.stopPointer.add(this.quantUnit);
      evt.stopTime = this.clock.getSeconds(evt.stopPointer);
    }
  }

  initializeMidi() {
    this.midiListener = new MidiInput()
      .addListener('noteOn', (message) => {
        this.eventRecord({
          type: 'noteOn',
          midiNumber: message.midiNumber,
          velocity: message.velocity,
        });
      })
      .addListener('noteOff', (message) => {
        this.eventRecord({
          type: 'noteOff',
          midiNumber: message.midiNumber,
          velocity: message.velocity,
        });
      });
  }

  destroy() {
    this.midiListener?.stopListeners();
    this.disableSpaceListener();
  }

  noteOn(evt?) {
    if (typeof evt !== 'undefined') evt.preventDefault();
    this.eventRecord({
      type: 'noteOn',
      midiNumber: 71,
    });
  }

  noteOff(evt?) {
    if (typeof evt !== 'undefined') evt.preventDefault();
    this.eventRecord({
      type: 'noteOff',
      midiNumber: 71,
    });
  }

  initSpaceListener() {
    Mousetrap.bind(
      'space',
      (evt) => {
        evt.preventDefault();
        if (evt.repeat) return;
        this.eventRecord({
          type: 'noteOn',
          midiNumber: 71,
        });
      },
      'keydown',
    );
    Mousetrap.bind(
      'space',
      (evt) => {
        evt.preventDefault();
        this.eventRecord({
          type: 'noteOff',
          midiNumber: 71,
        });
      },
      'keyup',
    );
  }

  disableSpaceListener() {
    Mousetrap.unbind('space', 'keydown');
    Mousetrap.unbind('space', 'keyup');
  }

  cloneEvents() {
    const clonedEvents: RecorderEvent[] = [];
    for (let i = 0; i < this.events.length; i++) {
      clonedEvents.push(cloneEvt(this.events[i]));
    }
    return clonedEvents;
  }

  setQuantUnit(quantUnit: string | Rhythm = this.quantUnit) {
    quantUnit = quantUnit instanceof Rhythm ? quantUnit : new Rhythm(quantUnit);
    this.quantUnit = quantUnit;
    const beat = this.meter.beatDuration;
    const quantsPerBeat = beat.fraction.value() / quantUnit.fraction.value();
    this.quantsPerBeat = quantsPerBeat;
    if (quantsPerBeat % 1 !== 0)
      throw new Error('NaQ: The quanitization unit must be a subdivision of the beat.');
    this.quantSec = this.clock.beatSec / quantsPerBeat;
  }
}

export default Recorder;
